import { type Decimal, dec, toFixedTrimmed, toStorage } from "./decimal";

/**
 * What an item's quantity measures. Fixed per item: an item never mixes dimensions, so on-hand is always a
 * single number.
 *
 * `pitch` is yeast pitching capacity: a liquid pitch sized for 7 bbl of wort is 7 bbl of capacity, and two
 * of them are 14. It is not a volume of yeast, so it is its own dimension rather than a kind of volume.
 */
export const DIMENSIONS = ["mass", "volume", "count", "pitch"] as const;
export type Dimension = (typeof DIMENSIONS)[number];

export const UNIT_IDS = [
  "g",
  "kg",
  "oz",
  "lb",
  "mL",
  "fl_oz",
  "L",
  "gal",
  "hL",
  "bbl",
  "each",
  "bbl_pitch",
] as const;
export type UnitId = (typeof UNIT_IDS)[number];

interface UnitDefinition {
  dimension: Dimension;
  label: string;
  /** How many canonical units one of this unit is. Exact by definition, never measured. */
  factor: string;
}

/**
 * Canonical units are grams, liters, each, and bbl of pitching capacity. Every factor is an exact
 * definition: 1 lb = 453.59237 g (international pound), 1 US gal = 3.785411784 L, and 1 US beer barrel =
 * 31 US gal.
 */
export const UNITS: Record<UnitId, UnitDefinition> = {
  g: { dimension: "mass", label: "g", factor: "1" },
  kg: { dimension: "mass", label: "kg", factor: "1000" },
  oz: { dimension: "mass", label: "oz", factor: "28.349523125" },
  lb: { dimension: "mass", label: "lb", factor: "453.59237" },
  mL: { dimension: "volume", label: "mL", factor: "0.001" },
  // A US fluid ounce, 1/128 of a gallon: how Ollie counts liquids such as ALDC (Scott, 2026-10-01).
  fl_oz: { dimension: "volume", label: "fl oz", factor: "0.0295735295625" },
  L: { dimension: "volume", label: "L", factor: "1" },
  gal: { dimension: "volume", label: "gal", factor: "3.785411784" },
  hL: { dimension: "volume", label: "hL", factor: "100" },
  bbl: { dimension: "volume", label: "bbl", factor: "117.347765304" },
  each: { dimension: "count", label: "each", factor: "1" },
  bbl_pitch: { dimension: "pitch", label: "bbl", factor: "1" },
};

export const CANONICAL_UNIT: Record<Dimension, UnitId> = {
  mass: "g",
  volume: "L",
  count: "each",
  pitch: "bbl_pitch",
};

/** Units offered for display and entry, in the order a US brewery reaches for them. */
export const DISPLAY_UNITS: Record<Dimension, readonly UnitId[]> = {
  mass: ["lb", "oz", "kg", "g"],
  volume: ["gal", "bbl", "L", "hL", "mL", "fl_oz"],
  count: ["each"],
  pitch: ["bbl_pitch"],
};

/** A pack is how stock arrives and gets counted: a 55 lb bag, a 500 g brick, a 7 bbl pitch. */
export interface Pack {
  /** Singular noun, e.g. "bag". */
  name: string;
  /** Canonical quantity inside one pack, as a decimal string. */
  size: string;
}

/** A quantity as a person typed it. `pack` means "packs of the relevant lot or item". */
export interface EnteredQuantity {
  value: string;
  unit: UnitId | "pack";
}

export class UnitError extends Error {}

/** Converts an entered quantity into the item's canonical unit, rounded to the storage scale. */
export function toCanonical(
  entered: EnteredQuantity,
  dimension: Dimension,
  pack?: Pack | null,
): string {
  const value = dec(entered.value);
  if (entered.unit === "pack") {
    if (!pack) throw new UnitError("This stock has no pack size, so it cannot be entered in packs");
    return toStorage(value.times(dec(pack.size)));
  }
  const unit = UNITS[entered.unit];
  if (unit.dimension !== dimension) {
    throw new UnitError(
      `${unit.label} measures ${unit.dimension}, but this item is measured by ${dimension}`,
    );
  }
  return toStorage(value.times(dec(unit.factor)));
}

/** Converts a canonical quantity into another unit of the same dimension. Exact up to division scale. */
export function fromCanonical(canonical: string | Decimal, unit: UnitId): Decimal {
  return dec(canonical).div(dec(UNITS[unit].factor));
}

/**
 * The unit brew day takes a pull in: anything weighed in pounds, a hop in ounces, and anything else in its
 * item's own unit.
 */
export function brewDayUnit(
  item: { dimension: Dimension; displayUnit: UnitId },
  hop: boolean,
): UnitId {
  if (item.dimension === "mass") return hop ? "oz" : "lb";
  return item.displayUnit;
}

/**
 * A pull as brew day shows it, and so as the brewer entered it: in brew day's unit, to the three places
 * its fields show. A brewer measures grain in bags or pounds, never grams (Scott, 2026-10-02).
 */
export function brewDayEntered(
  canonical: string,
  item: { dimension: Dimension; displayUnit: UnitId },
  hop: boolean,
): EnteredQuantity {
  const unit = brewDayUnit(item, hop);
  return { value: toFixedTrimmed(fromCanonical(canonical, unit), 3), unit };
}

export function packsIn(canonical: string | Decimal, pack: Pack): Decimal {
  return dec(canonical).div(dec(pack.size));
}

const DISPLAY_DIGITS: Record<UnitId, number> = {
  g: 1,
  kg: 3,
  oz: 2,
  lb: 2,
  mL: 0,
  fl_oz: 2,
  L: 2,
  gal: 2,
  hL: 3,
  bbl: 2,
  each: 0,
  bbl_pitch: 2,
};

/** "110 lb", "2.5 bags", "1 brick". Counts are whole unless the stock really holds a fraction. */
export function formatQuantity(
  canonical: string | Decimal,
  display: { unit: UnitId } | { pack: Pack },
): string {
  if ("pack" in display) {
    const packs = toFixedTrimmed(packsIn(canonical, display.pack), 2);
    return `${packs} ${plural(display.pack.name, packs)}`;
  }
  const value = fromCanonical(canonical, display.unit);
  const digits =
    display.unit === "each" && !value.eq(value.round(0)) ? 2 : DISPLAY_DIGITS[display.unit];
  return `${toFixedTrimmed(value, digits)} ${UNITS[display.unit].label}`;
}

export function plural(noun: string, amount: string): string {
  if (amount === "1" || amount === "-1") return noun;
  if (/(s|x|z|ch|sh)$/.test(noun)) return `${noun}es`;
  return `${noun}s`;
}
